<template>
  <div class="content">
    <h1 class="has-text-centered">Ohmsches Gesetz berechnen</h1>

    <div class="has-text-centered" v-if="calculators.length > 1">
      <label class="label">Welche Formel möchtest du benutzen?</label>
      <div class="field pb-2">
        <div class="dropdown is-300px-width" :class="{ 'is-active': dropdown_active }">
          <div class="dropdown-trigger is-300px-width">
            <button class="button is-fullwidth" @click="toggleDropdown()">
              <span v-if="target_calculator.title != ''">{{ target_calculator.title }}</span>
              <span v-else v-katex="target_calculator.katex_formula"></span>
              <span class="icon is-small">
                <i class="fas fa-angle-down" aria-hidden="true"></i>
              </span>
            </button>
          </div>
          <div class="dropdown-menu is-300px-width">
            <div class="dropdown-content">
              <div class="dropdown-item is-clickable" v-for="(calculator, index) in calculators" :key="index" @click="selectTargetCalculator(index)" v-show="index != target_calculator_index">
                <p v-if="calculator.title != ''">{{ calculator.title }}</p>
                <p v-else v-katex="calculator.katex_formula"></p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <DefaultCalculator v-for="(calculator, index) in calculators" :key="index" :prop_katex_formula="calculator.katex_formula" :prop_parser_formula="calculator.parser_formula" :prop_variables="calculator.variables" :prop_id="calculator.id" :prop_target_calculator_index="target_calculator_index" :prop_target_variable="getTargetVariable()" v-show="target_calculator_index == index" />

    <FeedbackBox />

    <GeneralAffiliateBox />

    <div class="box">
      <h2>Was ist das Ohmsche Gesetz?</h2>
      <p>
        Das Ohmsche Gesetz gehört zu den grundlegendsten Gesetzen der Elektrotechnik und beschreibt den direkten Zusammenhang der drei Grundgrößen eines Stromkreises:
        <ul>
          <li>Elektrische Spannung (U)</li>
          <li>Ohmscher Widerstand (R)</li>
          <li>Elektrischer Strom (I)</li>
        </ul>
        Wird Spannung an Leiter gelegt, dann verändert sich die hindurchfließende elektrische Stromstärke proportional zur Spannung. Je mehr Strom fließt desto höher wird die Spannung. Oder umgekehrt: Je weniger Strom fließt desto niedriger wird die Spannung. Der Zusammenhang wird mit folgender Formel beschrieben:
        <span v-katex="'U=R*I'"></span>
      </p>
      <p>
        Wichtig zu bedenken: Das Ohmsche Gesetz gilt nur in Zusammenhang mit ohmschen Widerständen. Als ohmschen Widerstand bezeichnet man einen Widerstand dann, wenn der Wert des Widerstandes nicht von der anliegenden Spannung oder dem fließenden Strom abhängt, sondern unabhängig von beiden Größen konstant ist.
      </p>
      <p>
        Mithilfe von Spannung, Strom und Widerstand lässt sich auch die elektrische Leistung berechnen - einen Rechner dafür findest du <router-link :to="{name: 'calculator-dc-power'}">hier</router-link>.
      </p>

      <h2>Wie lautet die Formel für das Ohmsche Gesetz?</h2>
      <p>
        Das Ohmsche Gesetz lässt sich mit folgender Formel berechnen:
      </p>
      <FormulaDescription v-for="(calculator, index) in calculators" :key="index" :katex_formula="calculator.katex_formula" :variables="calculator.variables" />

      <h2>Weiterführende Links</h2>
      <p>
        <ul class="fa-ul pb-3">
          <li>
            <span class="fa-li"><i class="fas fa-arrow-circle-right"></i></span>
            <a href="https://de.wikipedia.org/wiki/Ohmsches_Gesetz" target="_blank">Wikipedia-Artikel</a>
          </li>
        </ul>
      </p>

    </div>
  </div>
</template>

<script>
import DefaultCalculator from '@/components/DefaultCalculator.vue'
import FormulaDescription from '@/components/FormulaDescription.vue'
import FeedbackBox from '@/components/FeedbackBox.vue'
import GeneralAffiliateBox from '@/components/GeneralAffiliateBox.vue'

export default {
  name: 'calculator-ohms-law',
  components: {
    DefaultCalculator,
    FormulaDescription,
    FeedbackBox,
    GeneralAffiliateBox
  },
  metaInfo: {
    title: 'Ohmsches Gesetz Rechner | Elektrotechik-Rechner.at',
    meta: [
      {
        name: 'description',
        content: 'Das Ohmsche Gesetz (Formel: U=R*I) beschreibt den Zusammenhang zwischen Strom, Spannung und Widerstand. Hier einfach online berechnen.'
      }
    ]
  },
  created () {
    this.$store.commit('updateNavTitle', { title: '<span class="fas fa-angle-left"></span> Zurück', route: 'calculators' })
    this.target_calculator_index = this.getTargetCalculator()
  },
  methods: {
    toggleDropdown () {
      if (this.dropdown_active) {
        this.dropdown_active = false
      } else {
        this.dropdown_active = true
      }
    },
    selectTargetCalculator (index) {
      this.target_calculator_index = index
      this.dropdown_active = false
    },
    getValue (calcId, variable) {
      variable = variable.toLowerCase() + '_val'
      const reg = /^[+-]?[0-9]{1,16}(?:\.[0-9]{1,16})?$/
      if (parseInt(this.$route.query.calc, 10) === calcId) {
        for (const key in this.$route.query) {
          if (key === variable && reg.test(this.$route.query[key])) {
            return this.$route.query[key]
          }
        }
      }
      return 1
    },
    getMultiplicator (calcId, variable) {
      variable = variable.toLowerCase() + '_mul'
      const reg = /^[+-]?[0-9]{1,16}(?:\.[0-9]{1,16})?$/
      if (parseInt(this.$route.query.calc, 10) === calcId) {
        for (const key in this.$route.query) {
          if (key === variable && reg.test(this.$route.query[key])) {
            return Math.pow(10, this.$route.query[key])
          }
        }
      }
      return 1
    },
    getTargetVariable () {
      for (const i in this.calculators) {
        if (parseInt(this.$route.query.calc, 10) === this.calculators[i].id) {
          for (const key in this.$route.query) {
            if (key === 'target_var') {
              for (const y in this.calculators[i].variables) {
                if (this.calculators[i].variables[y].variable.toLowerCase() === this.$route.query[key]) {
                  return this.calculators[i].variables[y].label
                }
              }
            }
          }
        }
      }
      return null
    },
    getTargetCalculator () {
      for (const i in this.calculators) {
        if (parseInt(this.$route.query.calc, 10) === this.calculators[i].id) {
          return parseInt(i)
        }
      }
      return 0
    }
  },
  computed: {
    target_calculator: function () {
      return this.calculators[this.target_calculator_index]
    }
  },
  data () {
    return {
      dropdown_active: false,
      target_calculator_index: 0,
      calculators: [
        {
          id: 1,
          title: '',
          katex_formula: 'U=R*I',
          parser_formula: 'U-R*I',
          variables: [
            {
              variable: 'U',
              unit: {
                content: 'V',
                type: 'katex',
                has_prefix: true
              },
              label: {
                short: [
                  {
                    content: 'U',
                    type: 'katex'
                  }
                ],
                description: [
                  {
                    content: 'Elektrische Spannung in Volt [V]',
                    type: 'text'
                  }
                ]
              },
              component: 'DefaultUnitInput',
              varValue: {
                unitValue: this.getValue(1, 'U'),
                unitMultiplicator: this.getMultiplicator(1, 'U')
              },
              is_constant: false
            },
            {
              variable: 'R',
              unit: {
                content: '\\Omega',
                type: 'katex',
                has_prefix: true
              },
              label: {
                short: [
                  {
                    content: 'R',
                    type: 'katex'
                  }
                ],
                description: [
                  {
                    content: 'Ohmscher Widerstand in Ohm [Ω]',
                    type: 'text'
                  }
                ]
              },
              component: 'DefaultUnitInput',
              varValue: {
                unitValue: this.getValue(1, 'R'),
                unitMultiplicator: this.getMultiplicator(1, 'R')
              },
              is_constant: false
            },
            {
              variable: 'I',
              unit: {
                content: 'A',
                type: 'katex',
                has_prefix: true
              },
              label: {
                short: [
                  {
                    content: 'I',
                    type: 'katex'
                  }
                ],
                description: [
                  {
                    content: 'Elektrische Stromstärke in Ampere [A]',
                    type: 'text'
                  }
                ]
              },
              component: 'DefaultUnitInput',
              varValue: {
                unitValue: this.getValue(1, 'I'),
                unitMultiplicator: this.getMultiplicator(1, 'I')
              },
              is_constant: false
            }
          ]
        }
      ]
    }
  }
}
</script>
